/**
 * Búsqueda tolerante a errores ortográficos (fuzzy) sin dependencias externas.
 *
 * Estrategia:
 *  1. Normaliza texto (minúsculas, sin acentos/diacríticos, trim).
 *  2. Si el término aparece como substring -> coincide (rápido).
 *  3. Si no, compara por distancia de Levenshtein contra cada palabra del texto,
 *     tolerando una cantidad de errores proporcional al largo del término.
 */

export function normalizarTexto(valor: string | null | undefined): string {
    return (valor || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
}

export function distanciaLevenshtein(a: string, b: string): number {
    if (a === b) { return 0; }
    if (!a.length) { return b.length; }
    if (!b.length) { return a.length; }

    const fila: number[] = [];
    for (let j = 0; j <= b.length; j++) {
        fila[j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
        let anterior = fila[0];
        fila[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const temp = fila[j];
            const costo = a[i - 1] === b[j - 1] ? 0 : 1;
            fila[j] = Math.min(
                fila[j] + 1,
                fila[j - 1] + 1,
                anterior + costo,
            );
            anterior = temp;
        }
    }
    return fila[b.length];
}

/**
 * Tolerancia de errores permitida según el largo del término buscado.
 */
function tolerancia(largo: number): number {
    if (largo <= 3) { return 0; }
    if (largo <= 5) { return 1; }
    if (largo <= 8) { return 2; }
    return 3;
}

/**
 * Indica si `termino` coincide (fuzzy) dentro de `texto`.
 */
export function coincideFuzzy(texto: string, termino: string): boolean {
    const objetivo = normalizarTexto(termino);
    if (!objetivo) { return true; }

    const base = normalizarTexto(texto);
    if (base.includes(objetivo)) { return true; }

    const maxErrores = tolerancia(objetivo.length);
    if (maxErrores === 0) { return false; }

    const palabras = base.split(/\s+/);
    for (const palabra of palabras) {
        if (distanciaLevenshtein(palabra, objetivo) <= maxErrores) {
            return true;
        }
        if (palabra.length > objetivo.length) {
            for (let i = 0; i + objetivo.length <= palabra.length; i++) {
                const ventana = palabra.substr(i, objetivo.length);
                if (distanciaLevenshtein(ventana, objetivo) <= maxErrores) {
                    return true;
                }
            }
        }
    }
    return false;
}
